import React from 'react';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import type { XCSS } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { EmojiTitleSelection } from '@confluence/emoji-title/entry-points/EmojiTitleSelection.tsx';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { COVER_IMAGE_FIXED_WIDTH } from '@confluence/page-cover-picture/entry-points/pageCoverPictureConstants.ts';
import { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';

import type { TitleSectionProps } from './titleareaContentComponentsTypes.ts';

const mainBoxStylesBase = xcss({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

const mainBoxStylesGapSpace0 = xcss({
	gap: 'space.0',
});

const mainBoxStylesGapSpace300 = xcss({
	gap: 'space.300',
});

const titleBoxStylesBase = xcss({
	position: 'relative',
	width: '100%',
});

const titleBoxStylesDisplayFlex = xcss({
	display: 'flex',
});

const titleBoxStylesFlexDirectionColumn = xcss({
	flexDirection: 'column',
});

const titleBoxStylesFlexDirectionInitial = xcss({
	flexDirection: 'initial',
});

const titleBoxStylesGapSpace300 = xcss({
	gap: 'space.300',
});

const titleBoxStylesMarginTopMinus90px = xcss({
	// @ts-ignore TODO: refactor to remove need for hardcoded `-90px`
	marginTop: '-90px',
});

const titleBoxStylesMarginTopInitial = xcss({
	marginTop: 'initial',
});

const titleBoxStylesMarginBottomInitial = xcss({
	marginBottom: 'initial',
});

const titleBoxStylesAlignItemsCenter = xcss({
	alignItems: 'center',
});

const titleBoxStylesAlignItemsInitial = xcss({
	alignItems: 'initial',
});

const contentTopperBoxStylesBase = xcss({
	alignSelf: 'normal',
});

const contentTopperBoxStylesMarginTopSpaceNegative300 = xcss({
	marginTop: 'space.negative.300',
});

const emojiHiddenStyles = xcss({
	visibility: 'hidden',
});

const emojiHeightStyles = xcss({
	height: '88px',
});

const marginTopSpace0Styles = xcss({
	marginTop: 'space.0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiTitleSelectionWrapper = styled.div<{
	isTitleCenterAligned?: boolean;
	hasEmoji?: boolean;
	hasCoverPicture?: boolean;
	isSpaceOverview?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned || hasCoverPicture ? 'flex' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignItems: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexDirection: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned || hasCoverPicture ? 'column' : 'row',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ hasCoverPicture }) => (hasCoverPicture ? 'fit-content' : 'initial'),

	// Title & hover menu are moved down by 48px -- this styling negates that for the emoji
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ hasCoverPicture, hasEmoji, isSpaceOverview }) =>
		hasCoverPicture && (hasEmoji || isSpaceOverview) ? '-48px' : '0',
});
EmojiTitleSelectionWrapper.displayName = 'EmojiTitleSelectionWrapper';

const baseEditorTitleStyles = xcss({
	width: '100%',
});
const fixedWidthLeftAlignImageStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${COVER_IMAGE_FIXED_WIDTH}px`,
	margin: '0 auto',
	paddingLeft: 'space.250',
});

export const TitleSection = ({
	contentId,
	isTitleDisabled,
	linkCreation,
	editorTitle,
	isTemplateEditor,
	onUpdateStartEmoji,
	onUpdateEndEmoji,
	isTitleCenterAligned,
	hasCoverPicture,
	hasEmoji,
	isRemovingEmoji,
	contentTopperComponent,
	isReadOnly,
	coverPictureWidth,
	isPageContentFullWidth,
	isTitleAlignmentLoading,
	isSpaceOverview,
	pageTitleContent,
	autoPageEmojiCohort,
	isUnpublishedDraft,
	isEditorEmpty,
	emojiTitleDiscovered,
	contentType,
	aiSuggestedEmojiId,
}: TitleSectionProps) => {
	const isLivePage = useIsCurrentPageLive();

	const shouldBylineRenderContentTopper = isLivePage || !isSpaceOverview;

	const isContentTopperLayout = isTitleCenterAligned || hasCoverPicture;

	const isFixedWidth = coverPictureWidth === COVERPICTUREWIDTH.FIXED;

	const marginTopStyleForTitleWithCoverPicture = hasCoverPicture
		? titleBoxStylesMarginTopMinus90px
		: titleBoxStylesMarginTopInitial;

	const titleXcss: Array<XCSS | undefined> = [
		titleBoxStylesBase,
		// display
		titleBoxStylesDisplayFlex,
		// flexDirection
		isTitleCenterAligned || hasCoverPicture
			? titleBoxStylesFlexDirectionColumn
			: titleBoxStylesFlexDirectionInitial,
		// gap
		isTitleCenterAligned && hasEmoji && !hasCoverPicture ? titleBoxStylesGapSpace300 : undefined,
		// marginBottom
		titleBoxStylesMarginBottomInitial,
		// alignItems
		hasCoverPicture && isTitleCenterAligned
			? titleBoxStylesAlignItemsCenter
			: titleBoxStylesAlignItemsInitial,
		// marginTop
		marginTopStyleForTitleWithCoverPicture,
	];

	const titleSectionStyles = [
		mainBoxStylesBase,
		!hasCoverPicture ? mainBoxStylesGapSpace0 : mainBoxStylesGapSpace300,
	];

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
		<Box xcss={titleSectionStyles}>
			{!isContentTopperLayout && !shouldBylineRenderContentTopper && contentTopperComponent}
			<Box
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
				xcss={titleXcss}
				testId={isTitleCenterAligned ? 'title-wrapper-centered' : 'title-wrapper'}
			>
				{!isTemplateEditor && (!isRemovingEmoji || hasCoverPicture) && (
					<Box
						xcss={[
							hasCoverPicture && emojiHeightStyles,
							isRemovingEmoji && hasCoverPicture && emojiHiddenStyles,
							marginTopSpace0Styles,
							hasCoverPicture &&
								isFixedWidth &&
								isPageContentFullWidth &&
								!isTitleCenterAligned &&
								fixedWidthLeftAlignImageStyles,
						]}
					>
						{isTitleAlignmentLoading ? null : (
							<EmojiTitleSelectionWrapper
								data-testid={
									isTitleCenterAligned
										? 'emoji-title-selection-wrapper-centered'
										: 'emoji-title-selection-wrapper'
								}
								isTitleCenterAligned={isTitleCenterAligned}
								hasCoverPicture={hasCoverPicture}
								hasEmoji={hasEmoji}
								isSpaceOverview={isSpaceOverview}
							>
								<EmojiTitleSelection
									contentId={contentId}
									onUpdateStartEmoji={onUpdateStartEmoji}
									onUpdateEndEmoji={onUpdateEndEmoji}
									isTitleDisabled={isTitleDisabled}
									isTitleCenterAligned={isTitleCenterAligned}
									hasCoverPicture={!!hasCoverPicture}
									linkCreation={linkCreation}
									isReadOnly={isReadOnly}
									hasInitialEmoji={hasEmoji}
									pageTitleContent={pageTitleContent}
									autoPageEmojiCohort={autoPageEmojiCohort}
									isUnpublishedDraft={isUnpublishedDraft}
									isEditorEmpty={isEditorEmpty}
									emojiTitleDiscovered={emojiTitleDiscovered}
									contentType={contentType as string | undefined}
									aiSuggestedEmojiId={aiSuggestedEmojiId}
								/>
							</EmojiTitleSelectionWrapper>
						)}
					</Box>
				)}

				{isContentTopperLayout && !shouldBylineRenderContentTopper && contentTopperComponent && (
					<Box
						xcss={[
							contentTopperBoxStylesBase,
							(hasCoverPicture || (hasEmoji && !isRemovingEmoji)) &&
								!isSpaceOverview &&
								contentTopperBoxStylesMarginTopSpaceNegative300,
						]}
					>
						{contentTopperComponent}
					</Box>
				)}

				<Box xcss={[baseEditorTitleStyles]}>{editorTitle}</Box>
			</Box>
		</Box>
	);
};
TitleSection.displayName = 'TitleSection';
